import React, { useContext, useState, useEffect } from 'react';
import { Card, Table, Badge, Button, Spinner } from 'react-bootstrap';
import { FaGlobe, FaCheckCircle, FaCog, FaDownload, FaPlug, FaTrashAlt } from 'react-icons/fa';
import { useHistory } from 'react-router-dom';
import FetchContainer from 'Utils/FetchContainer';
import PageHeader from 'Utils/PageHeader';
import AppContext from 'Utils/AppContext';
import { STATUS } from 'Utils/styleTokens';
import { contrastColor } from 'contrast-color';
import { getAvailableIntegrations, importDataSources, testConnectionById } from './api';
import { getErrorMessage, getSourceColor, getSourceLabel } from 'Utils';
import IntegrationWizard from './IntegrationWizard';
import { listServices, deleteService, testService as testServiceById } from 'AdminPanel/servicesApi';
import { useConfirm } from 'Utils/ConfirmDialog';

// Integrations landing page — worker contract v1.3.
//
// Renders one row per pre-registered 3rd-party vendor the operator has wired up in
// service-registration.json. Each row exposes Configure, Import (once configured),
// and Test Connection actions.
//
// Backend source of truth: GET /api/integrations/available
const Integrations = () => {
  const { confirm, confirmDialog } = useConfirm();
  const [fetchingData, setFetchingData] = useState(false);
  const [available, setAvailable] = useState([]);
  const [wizardFor, setWizardFor] = useState(undefined); // undefined=closed, null=add, obj=edit
  const [importingId, setImportingId] = useState(null);
  const [testingId, setTestingId] = useState(null);
  const { setNotification, loadConnectionsHandler } = useContext(AppContext);
  const history = useHistory();

  const refresh = async () => {
    setFetchingData(true);
    try {
      // Drive the table from the merged registry (full instance detail: id, roles, configured,
      // connection echo, alg-map) and join the /available view for the import affordance
      // (integrationId + data-source-enumeration capability).
      const [instances, avail] = await Promise.all([
        listServices(),
        getAvailableIntegrations().catch(() => []),
      ]);
      const availById = new Map((avail || []).map((a) => [a.registeredServiceId, a]));
      const rows = (instances || [])
        .filter((s) => s.source === 'DB') // DB-backed 3rd-party instances (file core services excluded)
        .map((s) => {
          const a = availById.get(s.id) || {};
          return {
            ...s,
            registeredServiceId: s.id,
            vendorDisplayName: s.name || s.vendorDisplayName || s.vendor,
            // Vendor label for the colored pill (e.g. "Delphix") — distinct from the
            // instance display name rendered next to it.
            vendorLabel: s.vendorDisplayName || s.vendor || null,
            roleTypes: s.roles || [],
            integrationId: a.integrationId || null,
            supportsDataSourceEnumeration: a.supportsDataSourceEnumeration || (s.roles || []).includes('DISCOVER'),
          };
        });
      setAvailable(rows);
    } catch (e) {
      setNotification({ header: 'Failed to load integrations', body: <span>{getErrorMessage(e)}</span> });
    } finally {
      setFetchingData(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleError = (e) => {
    setNotification({ header: 'Integration error', body: <span>{getErrorMessage(e)}</span> });
  };

  const handleSaved = () => {
    setNotification({
      header: 'Integration saved',
      icon: <FaCheckCircle size='25px' color={STATUS.success.dot} />,
      body: <span>Registration and connection details stored.</span>,
    });
    refresh();
  };

  // Build the wizard's edit object from an available-card item. The card carries instance id
  // (registeredServiceId), vendor, roles, configured + connection echo (baseUrl/username) and the
  // persisted alg-map — all surfaced by the merged /api/admin/services list the card mirrors.
  const handleDelete = async (item) => {
    if (!await confirm({ title: `Delete integration "${item.vendorDisplayName}"?`, body: 'This removes its registration and stored credentials.', confirmLabel: 'Delete', variant: 'danger' })) return;
    try {
      await deleteService(item.registeredServiceId);
      setNotification({ header: 'Integration deleted', body: <span>Removed <strong>{item.vendorDisplayName}</strong>.</span> });
      refresh();
    } catch (e) {
      handleError(e);
    }
  };

  const handleImport = async (item) => {
    if (!item.integrationId) return;
    setImportingId(item.integrationId);
    try {
      const job = await importDataSources(item.integrationId);
      setNotification({
        header: 'Import started',
        icon: <FaGlobe size='25px' color={STATUS.success.dot} />,
        body: (
          <span>
            Import job <strong>#{job.jobId}</strong> pulled from{' '}
            <strong>{item.vendorDisplayName}</strong> is running in the background.{' '}
            <a
              href='#'
              onClick={(e) => {
                e.preventDefault();
                history.push('/sources');
              }}
            >
              View imported data sources →
            </a>
          </span>
        ),
      });
      if (loadConnectionsHandler) loadConnectionsHandler();
    } catch (e) {
      handleError(e);
    } finally {
      setImportingId(null);
    }
  };

  const handleTest = async (item) => {
    // Prefer the upstream-credential test (integration row) when configured; otherwise fall back
    // to the shim health probe by instance id. Both return { ok, message }.
    setTestingId(item.registeredServiceId);
    try {
      const result = item.integrationId
        ? await testConnectionById(item.integrationId)
        : await testServiceById(item.registeredServiceId);
      if (result.ok) {
        setNotification({
          header: 'Connection successful',
          icon: <FaCheckCircle size='25px' color={STATUS.success.dot} />,
          body: (
            <span>
              Reached <strong>{item.vendorDisplayName}</strong>.
              {result.message ? ` ${result.message}` : ''}
            </span>
          ),
        });
      } else {
        setNotification({
          header: 'Connection failed',
          body: (
            <span>
              Could not reach <strong>{item.vendorDisplayName}</strong>.
              {result.message ? ` ${result.message}` : ''}
            </span>
          ),
        });
      }
    } catch (e) {
      handleError(e);
    } finally {
      setTestingId(null);
    }
  };

  return (
    <div className='p-0 h-100'>
      {confirmDialog}
      <FetchContainer fetchingData={fetchingData} absolute={true}>
        <div>
          <PageHeader
            header='Integrations'
            icon={FaGlobe}
            tooltip='Register and configure the 3rd-party services this platform connects to.'
          />
          <Card className='shadow-sm mt-3'>
            <div className='d-flex justify-content-end p-2'>
              <Button variant='primary' size='sm' data-testid='add-integration'
                onClick={() => setWizardFor(null)}>
                + ADD INTEGRATION
              </Button>
            </div>
            <Table responsive hover className='mb-0 align-middle'>
              <thead>
                <tr>
                  <th style={{ width: '30%' }}>Name</th>
                  <th>Supports</th>
                  <th>Status</th>
                  <th style={{ width: 260 }} className='text-right'>Actions</th>
                </tr>
              </thead>
              <tbody>
                {available.length === 0 && !fetchingData && (
                  <tr>
                    <td colSpan={4} className='text-center text-muted py-5'>
                      No 3rd-party integrations are registered yet. Click
                      {' '}<strong>+ Add integration</strong> to register one — pick a service type,
                      name the instance, and enter its connection details in one step. You can
                      register the same type more than once (e.g. prod and QA).
                    </td>
                  </tr>
                )}
                {available.map((item) => (
                  <IntegrationRow
                    key={item.registeredServiceId}
                    item={item}
                    onConfigure={() => setWizardFor(item)}
                    onImport={() => handleImport(item)}
                    onTest={() => handleTest(item)}
                    onDelete={() => handleDelete(item)}
                    importing={!!item.integrationId && importingId === item.integrationId}
                    testing={testingId === item.registeredServiceId}
                  />
                ))}
              </tbody>
            </Table>
          </Card>
        </div>
      </FetchContainer>
      <IntegrationWizard
        show={wizardFor !== undefined}
        onHide={() => setWizardFor(undefined)}
        initial={wizardFor}
        onSaved={handleSaved}
        onError={handleError}
      />
    </div>
  );
};

const IntegrationRow = ({ item, onConfigure, onImport, onTest, onDelete, importing, testing }) => {
  const color = getSourceColor(item.registeredServiceId);
  const labelStyle = {
    backgroundColor: color,
    color: contrastColor({ bgColor: color }),
    padding: '2px 10px',
    borderRadius: 12,
    fontSize: '0.8rem',
    whiteSpace: 'nowrap',
  };
  // roles is a string array (e.g. ["DISCOVER","MASKING"]) from the merged registry list.
  const roleNames = (item.roleTypes || item.roles || [])
    .map((r) => (typeof r === 'string' ? r : r.type))
    .filter((t, i, a) => t && a.indexOf(t) === i);

  const supports = [];
  if (roleNames.length > 0) supports.push(roleNames.join(', '));
  if (item.supportsDataSourceEnumeration) supports.push('Data-source import');

  const canImport = item.configured && item.supportsDataSourceEnumeration && !!item.integrationId;
  const canTest = true; // shim health probe works by instance id even before credentials

  return (
    <tr>
      <td>
        <div className='d-flex align-items-center'>
          <span style={labelStyle} className='mr-2'>
            {item.vendorLabel || getSourceLabel(item.registeredServiceId)}
          </span>
          <strong>{item.vendorDisplayName}</strong>
        </div>
      </td>
      <td className='text-muted'>
        {supports.length > 0 ? supports.join(' • ') : '—'}
      </td>
      <td>
        {item.configured ? (
          <Badge variant='success'>Configured</Badge>
        ) : (
          <Badge variant='secondary'>Not configured</Badge>
        )}
      </td>
      <td>
        <div className='d-flex flex-column align-items-stretch' style={{ gap: '0.4rem' }}>
          <Button
            variant='outline-primary'
            size='sm'
            onClick={onConfigure}
          >
            <FaCog className='mr-2' />
            {item.configured ? 'Edit' : 'Configure'}
          </Button>
          <Button
            variant='outline-secondary'
            size='sm'
            onClick={onImport}
            disabled={!canImport || importing}
          >
            {importing ? (
              <Spinner as='span' size='sm' animation='border' className='mr-2' />
            ) : (
              <FaDownload className='mr-2' />
            )}
            Import
          </Button>
          <Button
            variant='outline-secondary'
            size='sm'
            onClick={onTest}
            disabled={!canTest || testing}
          >
            {testing ? (
              <Spinner as='span' size='sm' animation='border' className='mr-2' />
            ) : (
              <FaPlug className='mr-2' />
            )}
            Test connection
          </Button>
          <Button
            variant='outline-danger'
            size='sm'
            onClick={onDelete}
          >
            <FaTrashAlt className='mr-2' />
            Delete
          </Button>
        </div>
      </td>
    </tr>
  );
};

export default Integrations;
